'use client'

import { useT, useFormat, type Formatter, type TranslateFn } from '@/lib/i18n'

/**
 * Stage 36 — the scenarios 6–7 anomaly view, rebuilt to match scenarios 1–5.
 *
 * WHY THIS REPLACED THE CANDIDATE-CATALOG VIEW
 * The previous surface exposed the pipeline's internal vocabulary — candidate links,
 * competition clusters, selected/alternative roles, ML coverage and a review-scope
 * switch — none of which an analyst can act on, and several of which read as anomaly
 * signals while meaning nothing of the kind. This view shows the same rows in the same
 * shape scenarios 1–5 use: level cards, level/ML/search filters, one route.
 *
 * WHAT DID NOT CHANGE
 *   • no anomaly score, anomaly level, ML score or ML level is recomputed anywhere —
 *     every number is served verbatim from the catalog;
 *   • no candidate link was deleted: rows whose route cannot be stated are withheld from
 *     this user-facing surface and stay counted in the backend eligibility audit;
 *   • cluster context, alternatives, pairing strength and SHAP are still reachable —
 *     they live in the drawer and in the collapsed technical block, not in the table.
 *
 * EVERYTHING IS SERVER-SIDE. Eligibility, search, level filters, sorting, pagination and
 * the summary are resolved in SQL over ~2.16 M eligible rows. This component holds one
 * page at a time and never counts anything itself.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { cn } from '@/lib/utils'
import { formatWeightDifference, WEIGHT_DELTA_HEADER_KEY, WEIGHT_DELTA_TOOLTIP_KEY } from '../../weight-format'
import { useCrossUnitCasesList, useCrossUnitCasesSummary } from '../cross-unit.hooks'
import { reasonTitle } from '../cross-unit.i18n'
import {
  anomalyLevelLabels,
  ANOMALY_LEVEL_ORDER,
  mlLevelLabels,
  comparisonGroupLabel,
  CROSS_UNIT_DEFAULT_PAGE_SIZE,
  type AnomalyLevel,
  type CrossUnitCaseRow,
} from '../cross-unit.user-view'
import { CandidatePairDrawer } from './candidate-pair-drawer'
import { CandidateError } from './cross-unit-primitives'

/** Same note the scenarios 1–5 view carries, so both pages state the same caveat. */
/** Та же оговорка, что и на экране сценариев 1–5 — обе страницы говорят одно. */
const ANOMALY_NOTE_KEY = 'analytics.cu.anomalyNote'

const LEVEL_STYLE: Record<AnomalyLevel, { text: string; box: string }> = {
  priority_review: { text: 'text-risk-high', box: 'bg-risk-high-bg border-risk-high' },
  high: { text: 'text-risk-high', box: 'bg-risk-high-bg border-risk-high' },
  elevated: { text: 'text-risk-medium', box: 'bg-risk-medium-bg border-risk-medium' },
  baseline: { text: 'text-risk-low', box: 'bg-risk-low-bg border-risk-low' },
}

/**
 * Pairing status in plain Russian. The raw codes (EXACT / INFERRED_ONE_TO_ONE / AMBIGUOUS)
 * are never rendered: they are a matcher implementation detail that analysts read as a
 * verdict. The code still travels in the query and still works in search.
 */
/** Код статуса → ключ подписи. Перевод делается в компоненте: здесь module
 *  scope, хука нет, а код статуса уезжает в query и не должен зависеть от языка. */
const PAIRING_STATUS_KEYS: [string, string][] = [
  ['all', 'analytics.cu.pairingAny'],
  ['EXACT', 'analytics.cu.pairingConfirmed'],
  ['INFERRED_ONE_TO_ONE', 'analytics.cu.pairingSingle'],
  ['AMBIGUOUS', 'analytics.cu.pairingMultiple'],
]

function levelBadge(level: AnomalyLevel, t: TranslateFn) {
  const st = LEVEL_STYLE[level] ?? LEVEL_STYLE.baseline
  return (
    <span className={cn('inline-flex items-center rounded-[2px] border px-1.5 py-0.5 text-[10px] font-medium', st.box, st.text)}>
      {anomalyLevelLabels(t)[level] ?? level}
    </span>
  )
}

function mlBadge(level: string, t: TranslateFn) {
  // Переменная класса переименована в `tone`: прежнее имя `t` затеняло переводчик.
  const tone = level === 'high' ? 'text-risk-high' : level === 'medium' ? 'text-risk-medium' : 'text-risk-low'
  const labels = mlLevelLabels(t)
  return <span className={cn('font-medium', tone)}>{labels[level as keyof typeof labels] ?? level}</span>
}

function fmt(n: number | null, d = 2): string {
  return n === null || n === undefined ? '—' : n.toFixed(d)
}

export function CrossUnitCasesView({ scenarioId }: { scenarioId: 6 | 7 }) {
  const t = useT()
  const f = useFormat()
  const [anomalyLevel, setAnomalyLevel] = useState('all')
  const [mlLevel, setMlLevel] = useState('all')
  const [comparisonGroup, setComparisonGroup] = useState('all')
  const [pairingStatus, setPairingStatus] = useState('all')
  const [limitedHistory, setLimitedHistory] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showTechnical, setShowTechnical] = useState(false)

  // Search lives in the URL (?q=), exactly as on the scenarios 1–5 view, so a refresh or
  // Back keeps the query. The input stays local for responsiveness.
  const router = useRouter()
  const searchParams = useSearchParams()
  const urlQuery = searchParams.get('q') ?? ''
  const [search, setSearch] = useState(urlQuery)
  const debounced = useDebounce(search, 300)
  const lastWritten = useRef(urlQuery)

  useEffect(() => {
    if (debounced === lastWritten.current) return
    lastWritten.current = debounced
    const next = new URLSearchParams(Array.from(searchParams.entries()))
    if (debounced) next.set('q', debounced)
    else next.delete('q')
    const qs = next.toString()
    router.replace(qs ? `?${qs}` : '?', { scroll: false })
  }, [debounced, router, searchParams])

  useEffect(() => {
    if (urlQuery !== lastWritten.current) {
      lastWritten.current = urlQuery
      setSearch(urlQuery)
    }
  }, [urlQuery])

  const clearSearch = useCallback(() => setSearch(''), [])

  const [sortBy, setSortBy] = useState('anomalyScore')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(CROSS_UNIT_DEFAULT_PAGE_SIZE)
  const [selected, setSelected] = useState<string | null>(null)

  // ONE filter object for both requests: the cards can never describe a different
  // population than the table under them.
  const filters = useMemo(
    () => ({
      scenarioId,
      anomalyLevel: anomalyLevel === 'all' ? undefined : anomalyLevel,
      mlLevel: mlLevel === 'all' ? undefined : mlLevel,
      comparisonGroup: comparisonGroup === 'all' ? undefined : comparisonGroup,
      pairingStatus: pairingStatus === 'all' ? undefined : pairingStatus,
      limitedHistory: limitedHistory || undefined,
      search: debounced || undefined,
    }),
    [scenarioId, anomalyLevel, mlLevel, comparisonGroup, pairingStatus, limitedHistory, debounced],
  )

  const summary = useCrossUnitCasesSummary(filters)
  const list = useCrossUnitCasesList({ ...filters, page, pageSize, sortBy, sortDir })

  // Any filter change returns to page 1 — a stale offset must never be sent.
  const filterKey = JSON.stringify(filters) + pageSize
  const [lastKey, setLastKey] = useState(filterKey)
  if (filterKey !== lastKey) {
    setLastKey(filterKey)
    if (page !== 1) setPage(1)
  }

  const s = summary.data
  const total = list.data?.total ?? 0
  const totalPages = Math.max(1, list.data?.totalPages ?? Math.ceil(total / pageSize))
  const groupOptions = useMemo(() => {
    const keys = Object.keys(s?.byComparisonGroup ?? {})
    return [['all', t('analytics.cu.allGroups')] as [string, string], ...keys.map((k) => [k, comparisonGroupLabel(k, t)] as [string, string])]
  }, [s?.byComparisonGroup])

  const toggleSort = (key: string) => {
    if (sortBy === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    else {
      setSortBy(key)
      setSortDir('desc')
    }
  }

  const listError = list.isError
    ? list.error instanceof Error
      ? list.error.message
      : t('analytics.cu.scenarioUnavailable')
    : null

  return (
    <div className="space-y-5">
      <h2 className="text-[15px] font-semibold text-[var(--text-primary)]">{t('analytics.scenario.n', { n: scenarioId })}</h2>

      <div className="flex items-start gap-2 rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-3 py-2">
        <p className="text-[11px] leading-relaxed text-[var(--text-secondary)]">{t(ANOMALY_NOTE_KEY)}</p>
      </div>

      {/* Summary cards — the four model levels plus their total. Counts come from the
          backend over the same filtered population the table pages. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <Card label={t('analytics.cu.totalCases')} value={s ? f.int(s.totalCases) : '…'} />
        {ANOMALY_LEVEL_ORDER.map((lvl) => (
          <Card
            key={lvl}
            label={anomalyLevelLabels(t)[lvl]}
            value={s ? f.int(s.byAnomalyLevel[lvl] ?? 0) : '…'}
            accent={LEVEL_STYLE[lvl].text}
          />
        ))}
      </div>

      {/* Filters */}
      <div className="space-y-2 rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            label={t('analytics.cu.anomalyLevel')}
            value={anomalyLevel}
            onChange={setAnomalyLevel}
            options={[['all', t('analytics.cu.allLevels')], ...ANOMALY_LEVEL_ORDER.map((l) => [l, anomalyLevelLabels(t)[l]] as [string, string])]}
          />
          <Select
            label={t('analytics.cu.mlLevel')}
            value={mlLevel}
            onChange={setMlLevel}
            options={[['all', t('common.value.all')], ['high', t('common.risk.high')], ['medium', t('common.risk.medium')], ['low', t('common.risk.low')]]}
          />
          <div className="relative flex min-w-[260px] flex-1 items-center">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('analytics.cu.searchPlaceholder')}
              aria-label={t('analytics.cu.searchAria')}
              className="h-8 w-full rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-canvas)] px-2.5 pr-14 text-[12px] text-[var(--text-primary)] focus-visible:border-[var(--accent-data)] focus-visible:outline-none"
            />
            {search !== debounced || (search && list.isFetching) ? (
              <span className="absolute right-7 text-[10px] text-[var(--text-tertiary)]" aria-hidden="true">…</span>
            ) : null}
            {search && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label={t('common.action.clearSearch')}
                className="absolute right-1.5 rounded-[3px] px-1.5 py-0.5 text-[12px] text-[var(--text-tertiary)] hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)]"
              >
                ✕
              </button>
            )}
          </div>
          <button
            type="button"
            data-block="advanced-filters-toggle"
            aria-expanded={showAdvanced}
            onClick={() => setShowAdvanced((v) => !v)}
            className="rounded-[3px] border border-[var(--border-default)] px-2 py-1 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
          >
            {t('analytics.cu.advancedFilters')} {showAdvanced ? '▲' : '▼'}
          </button>
        </div>

        {/* Collapsed by default: these narrow the population by how the legs were matched,
            which is analyst-relevant but not part of the everyday level/ML/search flow. */}
        {showAdvanced && (
          <div data-block="advanced-filters" className="flex flex-wrap items-center gap-2 border-t border-[var(--border-subtle)] pt-2">
            <Select label={t('analytics.cu.comparisonGroup')} value={comparisonGroup} onChange={setComparisonGroup} options={groupOptions} />
            <Select label={t('analytics.cu.pairingStatus')} value={pairingStatus} onChange={setPairingStatus} options={PAIRING_STATUS_KEYS.map(([code, key]) => [code, t(key)] as [string, string])} />
            <label className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">
              <input type="checkbox" checked={limitedHistory} onChange={(e) => setLimitedHistory(e.target.checked)} />
              {t('analytics.cases.limitedHistoryOnly')}
            </label>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-[4px] border border-[var(--border-subtle)]">
        <div className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-3 py-2 text-[11px] text-[var(--text-tertiary)]">
          <span data-block="result-count">
            {list.isLoading ? t('common.state.loading') : t('analytics.cu.casesTotal', { count: total })}
          </span>
          <span>{t('analytics.scenario.n', { n: scenarioId })}</span>
        </div>
        {listError ? (
          <div className="p-3">
            <CandidateError
              message={listError}
              code={(list.error as { errorCode?: string } | undefined)?.errorCode}
            />
          </div>
        ) : (
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-[var(--border-subtle)] bg-[var(--bg-subtle)] text-left text-[10px] uppercase tracking-[0.04em] text-[var(--text-tertiary)]">
                <Th>{t('analytics.cu.colCaseScenario')}</Th>
                <Th onClick={() => toggleSort('anomalyScore')} active={sortBy === 'anomalyScore'} dir={sortDir}>{t('analytics.cu.colAnomaly')}</Th>
                <Th>{t('analytics.cu.anomalyLevel')}</Th>
                <Th>{t('analytics.cu.comparisonGroup')}</Th>
                <Th onClick={() => toggleSort('mlScore')} active={sortBy === 'mlScore'} dir={sortDir}>ML</Th>
                <Th>{t('common.field.cargo')}</Th>
                <Th>{t('common.field.route')}</Th>
                <Th onClick={() => toggleSort('weightDiffPct')} active={sortBy === 'weightDiffPct'} dir={sortDir}
                  title={t(WEIGHT_DELTA_TOOLTIP_KEY)}>{t(WEIGHT_DELTA_HEADER_KEY)}</Th>
                <Th onClick={() => toggleSort('daysBetween')} active={sortBy === 'daysBetween'} dir={sortDir}>{t('analytics.col.days')}</Th>
                <Th>{t('analytics.cu.colWhyUnusual')}</Th>
              </tr>
            </thead>
            <tbody>
              {list.data?.rows.map((r) => (
                <Row key={r.caseId} r={r} onClick={() => setSelected(r.caseId)} />
              ))}
              {!list.isLoading && list.data && list.data.rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-[var(--text-tertiary)]">
                    {debounced ? (
                      <>
                        <div className="text-[13px] text-[var(--text-secondary)]">{t('analytics.cases.searchEmptyTitle', { query: debounced })}</div>
                        <div className="mt-1 text-[11px]">
                          {t('analytics.cases.searchEmptyHintCu')}
                        </div>
                        <button type="button" onClick={clearSearch} className="mt-2 rounded-[3px] border border-[var(--border-default)] px-2 py-1 text-[11px] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]">
                          {t('common.action.clearSearch')}
                        </button>
                      </>
                    ) : (
                      t('analytics.cu.emptyFiltered')
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {/* Pagination — page and totals come from the server response. */}
        <div className="flex items-center justify-between border-t border-[var(--border-subtle)] px-3 py-2 text-[11px] text-[var(--text-secondary)]">
          <div className="flex items-center gap-2">
            <span>{t('analytics.cu.perPage')}</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-7 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-canvas)] px-1.5 text-[11px]">
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-[3px] border border-[var(--border-default)] px-2 py-1 disabled:opacity-40">{t('analytics.cu.prev')}</button>
            <span>{t('analytics.cu.pageOf', { page: list.data?.page ?? page, total: totalPages })}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-[3px] border border-[var(--border-default)] px-2 py-1 disabled:opacity-40">{t('analytics.cu.next')}</button>
          </div>
        </div>
      </div>

      {/* Technical details — collapsed, for developers. Everything the old header, the
          coverage block and the scope switch used to shout at the analyst lives here. */}
      <div className="rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-surface)]">
        <button
          type="button"
          data-block="technical-details-toggle"
          aria-expanded={showTechnical}
          onClick={() => setShowTechnical((v) => !v)}
          className="w-full px-3 py-2 text-left text-[11px] text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
        >
          {t('analytics.cu.technicalDetails')} {showTechnical ? '▲' : '▼'}
        </button>
        {showTechnical && (
          <dl data-block="technical-details" className="grid grid-cols-1 gap-1 border-t border-[var(--border-subtle)] px-3 py-2 text-[11px] text-[var(--text-secondary)] sm:grid-cols-2">
            <TechRow label={t('analytics.cu.contract')} value={s?.version ?? '—'} />
            <TechRow label={t('analytics.cu.casesUnderFilters')} value={s ? f.int(s.totalCases) : '—'} />
            <TechRow label={t('analytics.cu.levelsSum')} value={s ? f.int(s.levelSum) : '—'} />
            <TechRow label={t('analytics.cu.pairingGroups')} value={s ? f.int(s.distinctClusters) : '—'} />
            <TechRow label={t('analytics.cu.withWithoutReasons')}
              value={s ? `${f.int(s.reasonsWithCount)} / ${f.int(s.reasonsWithoutCount)}` : '—'} />
            <TechRow label={t('analytics.cu.sourceRows')}
              value={sourceRowsText(s?.eligibility, scenarioId, f)} />
            <TechRow label={t('analytics.cu.withReadableRoute')}
              value={eligibleRowsText(s?.eligibility, scenarioId, f)} />
            <TechRow label={t('analytics.cu.excludedRoute')}
              value={excludedRowsText(s?.eligibility, scenarioId, f)} />
          </dl>
        )}
      </div>

      <CandidatePairDrawer pairId={selected} scenarioId={scenarioId} onClose={() => setSelected(null)} />
    </div>
  )

  function Row({ r, onClick }: { r: CrossUnitCaseRow; onClick: () => void }) {
    // The SAME formatter the scenarios 1–5 table uses. Scenarios 6–7 carry no stored
    // percentage, so it is recomputed from the two weights by the documented formula.
    const weight = formatWeightDifference({
      importWeight: r.importWeight,
      exportWeight: r.exportWeight,
      weightDiffPct: r.weightDiffPct,
    }, t)
    const reasons = r.anomalyReasons.slice(0, 2).map((c) => reasonTitle(c, t))
    const more = r.anomalyReasons.length - reasons.length
    return (
      <tr
        data-case-id={r.caseId}
        onClick={onClick}
        className="cursor-pointer border-b border-[var(--border-subtle)] hover:bg-[var(--bg-subtle)]"
      >
        <td className="px-3 py-2">
          {/* Short id only: the 32-char canonical id is technical, and the full value is
              in the title and in the drawer. It stays searchable either way. */}
          <div className="font-mono text-[12px] text-[var(--text-primary)]" title={r.caseId}>
            #{r.caseId.slice(0, 8)}
          </div>
          <div className="text-[10px] text-[var(--text-tertiary)]">{t('analytics.scenario.n', { n: r.scenarioId })}</div>
        </td>
        <td className="px-3 py-2 font-mono tabular-nums text-[var(--text-primary)]">{fmt(r.anomalyScore)}</td>
        <td className="px-3 py-2">{levelBadge(r.anomalyLevel, t)}</td>
        <td className="px-3 py-2 text-[11px] text-[var(--text-secondary)]">{comparisonGroupLabel(r.comparisonGroup, t)}</td>
        <td className="px-3 py-2 font-mono tabular-nums">{fmt(r.mlScore)} · {mlBadge(r.mlLevel, t)}</td>
        <td className="px-3 py-2 text-[var(--text-secondary)]">{r.cargo ?? '—'}</td>
        <td className="px-3 py-2 text-[11px] text-[var(--text-secondary)]">{r.route ?? '—'}</td>
        <td className="px-3 py-2 font-mono tabular-nums" title={weight.tooltip}>{weight.displayValue}</td>
        <td className="px-3 py-2 font-mono tabular-nums">{r.daysBetween ?? '—'}</td>
        <td className="px-3 py-2">
          {reasons.length ? (
            <div className="flex flex-wrap gap-1">
              {/* параметр стрелки назван reason, а не t: в этом компоненте
                  t — переводчик из useT(), и прежнее имя его затеняло. */}
              {reasons.map((reason) => (
                <span key={reason.code} title={reason.hint || undefined}
                  className="rounded-[2px] bg-[var(--bg-subtle)] px-1.5 py-0.5 text-[10px] text-[var(--text-secondary)]">
                  {reason.title}
                </span>
              ))}
              {more > 0 && <span className="text-[10px] text-[var(--text-tertiary)]">+{more}</span>}
            </div>
          ) : (
            <span className="text-[10px] text-[var(--text-tertiary)]">{t('analytics.cu.noReason')}</span>
          )}
        </td>
      </tr>
    )
  }
}

/* ── eligibility audit readers (technical block only) ─────────────────────── */

type Audit = { byScenario?: Record<string, { sourceRows: number; eligibleRows: number; excludedRows: number }> }

function auditFor(a: Audit | undefined, scenarioId: number) {
  return a?.byScenario?.[String(scenarioId)]
}
function sourceRowsText(a: Audit | undefined, sc: number, f: Formatter): string {
  const v = auditFor(a, sc)
  return v ? f.int(v.sourceRows) : '—'
}
function eligibleRowsText(a: Audit | undefined, sc: number, f: Formatter): string {
  const v = auditFor(a, sc)
  return v ? f.int(v.eligibleRows) : '—'
}
function excludedRowsText(a: Audit | undefined, sc: number, f: Formatter): string {
  const v = auditFor(a, sc)
  return v ? f.int(v.excludedRows) : '—'
}

/* ── small presentational parts (mirrors of the scenarios 1–5 view) ───────── */

function Th({ children, onClick, active, dir, title }: {
  children: React.ReactNode
  onClick?: () => void
  active?: boolean
  dir?: 'asc' | 'desc'
  title?: string
}) {
  return (
    <th title={title}
      className={cn('px-3 py-2 font-medium', onClick && 'cursor-pointer select-none hover:text-[var(--text-secondary)]',
        title && 'decoration-dotted underline-offset-2 [text-decoration-line:underline]')}
      onClick={onClick}>
      {children}{active && <span className="ml-0.5">{dir === 'desc' ? '↓' : '↑'}</span>}
    </th>
  )
}

function Card({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.04em] text-[var(--text-tertiary)]">{label}</div>
      <div className={cn('mt-1 font-semibold tabular-nums text-[20px]', accent ?? 'text-[var(--text-primary)]')}>{value}</div>
    </div>
  )
}

function TechRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-[var(--text-tertiary)]">{label}</dt>
      <dd className="font-mono tabular-nums">{value}</dd>
    </div>
  )
}

function Select({ label, value, onChange, options }: {
  label: string
  value: string
  onChange: (v: string) => void
  options: [string, string][]
}) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-[var(--text-tertiary)]">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-canvas)] px-2 text-[12px] text-[var(--text-primary)] focus-visible:border-[var(--accent-data)] focus-visible:outline-none">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  )
}

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}
